import { CommonModule } from "@angular/common";
import { Component, inject, OnDestroy } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { EventsManagementService } from "@services/events-management.service";
import { SelectedCityService } from "@services/selected-city.service";
import { EventInfoResponseBody } from "@services/types/events-management-response.type";
import { AutoCompleteModule } from "primeng/autocomplete";
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { environment } from "../../../../environments/environment";

type CompleteMethodEvent = { query: string };

@Component({
    selector: "app-events-search-input",
    imports: [
        AutoCompleteModule,
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
    ],
    templateUrl: "./events-search-input.component.html",
    styleUrl: "./events-search-input.component.scss"
})
export class EventsSearchInputComponent implements OnDestroy {
  private readonly eventsService = inject(EventsManagementService);
  private readonly selectedCityService = inject(SelectedCityService);
  private readonly router = inject(Router);

  searchControl = new FormControl<string>("", { nonNullable: true });
  isLoading = false;
  private readonly searchQuery$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  private readonly selectedCity$ = toObservable(
    this.selectedCityService.selectedCity,
  );

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  eventSuggestions$: Observable<EventInfoResponseBody[]> = combineLatest([
    this.searchQuery$.pipe(
      startWith(""),
      debounceTime(500),
      map((q) => (q ?? "").trim()),
      distinctUntilChanged(),
    ),
    this.selectedCity$.pipe(distinctUntilChanged()),
  ]).pipe(
    tap(() => (this.isLoading = true)),
    switchMap(([query, city]) =>
      this.eventsService
        .getAllEvents({
          query: query || undefined,
          city: city ?? undefined,
        })
        .pipe(
          map((response) => response.data ?? []),
          catchError((err) => {
            console.error(err);
            return of([] as EventInfoResponseBody[]);
          }),
          finalize(() => (this.isLoading = false)),
        ),
    ),
    takeUntil(this.destroy$),
  );

  handleCompletMethod(event: CompleteMethodEvent) {
    this.searchQuery$.next(event.query);
  }

  openEvent(event: EventInfoResponseBody) {
    this.searchControl.setValue("");
    this.router.navigateByUrl(`/event/${event.id}`);
  }

  loadEventImage(event_thumb: string) {
    if (event_thumb !== "" && event_thumb !== "FALTA IMPLEMENTAR") {
      const image = `${environment.BLOB_URL}/${event_thumb}`;

      return image;
    }
    return event_thumb;
  }
}
